import React, { useState, useMemo, useCallback, useEffect } from "react";
import { navigate } from "@reach/router";
import { FiPlus } from "react-icons/fi";
import { Spinner } from "@chakra-ui/react";
import { Modal } from "newui/components/ui/Modal";
import { Button } from "newui/components/Button";
import { Pagination } from "newui/components/Pagination/Pagination";
import { useNavigationPaths } from "newui/features/Auth/hooks/useNavigationPaths";
import { useCurrentCompany } from "hooks/auth";
import { ReadOnlyGuard, useIsReadOnlyMode } from "newui/features/ExecutiveView";
import { SortableKeyPersonnelList } from "./components/SortableKeyPersonnelList";
import { useKeyPersonnelList } from "./hooks/useKeyPersonnel";
import {
	useDeleteKeyPersonnel,
	useReorderKeyPersonnel,
} from "./hooks/useKeyPersonnelMutations";
import type { KeyPersonnelDetail } from "./types/KeyPersonnel.type";

const DEFAULT_ROWS_PER_PAGE = 10;

interface KeyPersonnelListPageProps {
	path?: string;
	/**
	 * PRO-709 — whether the viewer's RBAC v2 standing grants `company_profile`
	 * edit. Required rather than optional-defaulting-true so that a new call site
	 * cannot silently re-expose the Add/Edit/Delete controls to a view-only role;
	 * omitting it is a typecheck error. The server enforces the same capability on
	 * the key-personnel write routes — hiding a button is not access control.
	 */
	canEdit: boolean;
}

const KeyPersonnelListPage: React.FC<KeyPersonnelListPageProps> = ({ canEdit }) => {
	const [search, setSearch] = useState("");
	const [debouncedSearch, setDebouncedSearch] = useState("");
	const [sectorFilter, setSectorFilter] = useState<number[]>([]);
	const [deleteTarget, setDeleteTarget] = useState<KeyPersonnelDetail | null>(null);
	const [currentPage, setCurrentPage] = useState(1);
	const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);

	const { memberPaths } = useNavigationPaths();
	const keyPersonnelPaths = memberPaths?.companyProfile;
	const currentCompany = useCurrentCompany();
	const profileTypeId = currentCompany?.profile_type?.id ?? 2;

	const isReadOnly = useIsReadOnlyMode();

	useEffect(() => {
		const timer = setTimeout(() => setDebouncedSearch(search), 300);
		return () => clearTimeout(timer);
	}, [search]);

	const listParams = useMemo(
		() => ({
			page: currentPage,
			per_page: rowsPerPage,
			search: debouncedSearch.trim() || undefined,
			...(sectorFilter.length > 0 && { filters: { sector: sectorFilter } }),
		}),
		[currentPage, rowsPerPage, debouncedSearch, sectorFilter],
	);

	const { data, isLoading } = useKeyPersonnelList(listParams);
	const personnel = useMemo(() => data?.key_personnels ?? [], [data]);
	const totalItems = data?.pagination?.total ?? 0;

	const sectorOptions = useMemo(() => {
		const group = data?.filters?.find((f) => f.name === "sector");
		return (group?.item ?? [])
			.map((opt) => ({ value: Number(opt.value), label: opt.label }))
			.filter((opt) => Number.isFinite(opt.value));
	}, [data?.filters]);

	const handleSectorFilterChange = useCallback((ids: number[]) => {
		setSectorFilter(ids);
		setCurrentPage(1);
	}, []);

	const deleteMutation = useDeleteKeyPersonnel();
	const reorderMutation = useReorderKeyPersonnel();

	// Reset to page 1 when search changes
	const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
		setSearch(e.target.value);
		setCurrentPage(1);
	}, []);

	// Keep page in range if the server total shrinks (e.g. after deletes)
	useEffect(() => {
		if (totalItems === 0) return;
		const lastPage = Math.max(1, Math.ceil(totalItems / rowsPerPage));
		if (currentPage > lastPage) {
			setCurrentPage(lastPage);
		}
	}, [totalItems, rowsPerPage, currentPage]);

	const handlePageChange = useCallback((page: number) => {
		setCurrentPage(page);
	}, []);

	const handleRowsPerPageChange = useCallback((newRowsPerPage: number) => {
		setRowsPerPage(newRowsPerPage);
		setCurrentPage(1);
	}, []);

	const handleEdit = useCallback((id: number) => {
		if (isReadOnly) return;
		if (keyPersonnelPaths) {
			navigate(keyPersonnelPaths.keyPersonnelEdit(id));
		}
	}, [isReadOnly, keyPersonnelPaths]);

	const handleDelete = useCallback((id: number) => {
		if (isReadOnly) return;
		const person = personnel.find((p) => p.id === id) ?? null;
		setDeleteTarget(person);
	}, [isReadOnly, personnel]);

	const handleConfirmDelete = useCallback(() => {
		if (!deleteTarget || deleteMutation.isPending) {
			return;
		}
		deleteMutation.mutate(deleteTarget.id, {
			onSuccess: () => setDeleteTarget(null),
		});
	}, [deleteTarget, deleteMutation]);

	const handleReorder = useCallback(
		(id: number, newSortOrder: number) => {
			reorderMutation.mutate({ id, sortOrder: newSortOrder });
		},
		[reorderMutation]
	);

	if (isLoading) {
		return (
			<div className="flex items-center justify-center py-16" data-testid="loading-state">
				<Spinner size="lg" />
			</div>
		);
	}

	return (
		<div className="flex flex-col gap-4" data-testid="key-personnel-list-page">
			{/* Header */}
			<div className="flex items-center justify-between">
				<h2 className="text-lg font-semibold text-gray-900">Key Personnel</h2>
				<div className="flex items-center gap-3">
					{/* Search */}
					<input
						type="text"
						value={search}
						onChange={handleSearchChange}
						placeholder="Search by name or position..."
						className="w-64 rounded border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
						data-testid="search-input"
					/>

					{/* Add button */}
					{canEdit && (
					<ReadOnlyGuard>
						<button
							type="button"
							onClick={() => keyPersonnelPaths && navigate(keyPersonnelPaths.keyPersonnelAdd)}
							className="flex items-center gap-2 rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
							data-testid="add-key-personnel-btn"
						>
							<FiPlus size={16} />
							Add Key Personnel
						</button>
					</ReadOnlyGuard>
					)}
				</div>
			</div>

			{/* Content */}
			{personnel.length === 0 ? (
				(debouncedSearch.trim() || sectorFilter.length > 0) ? (
					<div
						className="flex flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-200 py-16"
						data-testid="no-results-state"
					>
						<p className="mb-2 text-lg font-medium text-gray-600">
							No matching key personnel
						</p>
						<p className="text-sm text-gray-400">
							Try clearing your search or sector filter.
						</p>
					</div>
				) : (
					<div
						className="flex flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-200 py-16"
						data-testid="empty-state"
					>
						<p className="mb-2 text-lg font-medium text-gray-600">
							No key personnel yet
						</p>
						<p className="mb-4 text-sm text-gray-400">
							Add your first key personnel to get started
						</p>
						{canEdit && (
						<ReadOnlyGuard>
							<button
								type="button"
								onClick={() => keyPersonnelPaths && navigate(keyPersonnelPaths.keyPersonnelAdd)}
								className="flex items-center gap-2 rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
							>
								<FiPlus size={16} />
								Add Key Personnel
							</button>
						</ReadOnlyGuard>
						)}
					</div>
				)
			) : (
				<>
					<SortableKeyPersonnelList
						personnel={personnel}
						profileTypeId={profileTypeId}
						sectorOptions={sectorOptions}
						sectorFilter={sectorFilter}
						onSectorFilterChange={handleSectorFilterChange}
						onEdit={handleEdit}
						onDelete={handleDelete}
						canEdit={canEdit}
						onReorder={handleReorder}
					/>
					<Pagination
						currentPage={currentPage}
						rowsPerPage={rowsPerPage}
						totalItems={totalItems}
						onPageChange={handlePageChange}
						onRowsPerPageChange={handleRowsPerPageChange}
						showRowsPerPage
						rowsPerPageOptions={[10, 20, 50]}
						testId="key-personnel-pagination"
					/>
				</>
			)}

			{/* Delete confirmation modal */}
			<Modal
				isOpen={!!deleteTarget}
				onClose={() => setDeleteTarget(null)}
				title="Delete Key Personnel"
				description="Please confirm deletion"
				iconType="warning"
				titleColor="red.600"
				size="md"
				customFooter={
					<div className="flex w-full justify-between gap-2">
						<Button
							onClick={() => setDeleteTarget(null)}
							btntype="base"
							className="!w-full"
							disabled={deleteMutation.isPending}
						>
							Cancel
						</Button>
						<Button
							className="!w-full"
							btntype="third-danger"
							onClick={handleConfirmDelete}
							disabled={deleteMutation.isPending}
							data-testid="confirm-delete-button"
						>
							{deleteMutation.isPending ? <Spinner size="sm" /> : "Delete"}
						</Button>
					</div>
				}
			>
				{deleteTarget && (
					<p className="text-sm text-gray-600" data-testid="delete-confirmation-text">
						Are you sure you want to delete{" "}
						<span className="font-medium">{deleteTarget.full_name}</span>?
						This action cannot be undone.
					</p>
				)}
			</Modal>
		</div>
	);
};

export { KeyPersonnelListPage };
